<template>
	<div class="blueprint-editor">
		<h2 class="content-block" style="display:flex;flex-direction:row;justify-content:space-between;align-items:center;">
			<span style="flex-grow:1;">Blueprints Management</span>
			<DxDropDownButton :items="keyspecVisibilityOptions" :icon="keyspecVisibilityDisplay.icon" :text="keyspecVisibilityDisplay.text" @item-click="keyspecVisibility = $event.itemData.id" />
		</h2>
		<DxLoadPanel :visible="isBusy" shading-color="rgba(0, 0, 0, 0.2)" container=".blueprint-editor" />
		<DxSplitter height="calc(100vh - 250px)" @resize-end="resizedSplitter">
			<DxItem size="700px">
				<div class="blueprint-tree-container">
					<DxSortable :allow-ordering="true" filter=".dx-treeview-item" @drag-change="treeDragChange" @drag-end="treeDragEnd">
					<DxTreeView :data-source="schemaTree" item-template="blueprintTreeTemplate" class="blueprint-tree" items-expr="fields" key-expr="keyspec" expanded-expr="isExpanded" ref="blueprintTreeComponent"
						:select-by-click="true" selection-mode="single" selection-expr="selected" :search-enabled="true" :search-expr="['keyspec', 'singular', 'label' ]" @itemSelectionChanged="blueprintTreeSelected">
						<template #blueprintTreeTemplate="item">
							<span :class="{ newFieldLink: item.data.internal }" :disabled-draggable="!item.data.fieldtype && !item.data.internal" :data-id="item.data.id">
								<i v-if="item.data.fieldtype" :class="'dx-icon-' + fieldTypeMeta(item.data.fieldtype).icon"></i>
								<span>{{ item.data.singular ? item.data.singular : item.data.label }}</span>
								<span class="blueprint-keyspec" v-if="keyspecVisibility != 'hide' && !item.data.internal">
									({{ keyspecVisibility == 'show' || !item.data.keyspec.includes('.') ? item.data.keyspec : item.data.keyspec.split('.').slice(1).join('.') }})
								</span>
							</span>
						</template>
					</DxTreeView>
					</DxSortable>
					<div class="blueprint-editor-container">
						<FieldEditor ref="editorField" v-if="selectedItem && !selectedItemIsSchema && !selectedItemIsNewField && selectedItem.fieldtype" :field="selectedItem" @cancel="selectedItem = null" @set-busy="isBusy = $event" @edited="editedField" @deleted="deletedField" />
						<FieldCreator v-if="selectedItem && selectedItemIsNewField" :schema-id="selectedItem.schema_id" @set-busy="isBusy = $event" @created="createdNewField" @cancel="selectedItem = null" />
						<SchemaEditor ref="editorSchema" v-if="selectedItem && selectedItemIsSchema" :schema="selectedItem" @set-busy="isBusy = $event" @cancel="selectedItem = null" @edited="changedSchema" @deleted="deletedSchema" />
					</div>
				</div>
			</DxItem>
			<DxItem>
				<div>
					<DxTabs :selectedIndex="0">
						<DxItem icon="columnfield" text="Data Model" :active="true"></DxItem>
						<DxItem icon="pinleft" text="User Interface"></DxItem>
						<DxItem icon="runner" text="Actions"></DxItem>
					</DxTabs>
					<div style="position: absolute; z-index: 100; top: 75px; left: 5px; background: floralwhite; padding: 5px; user-select: none;">Hold Space + Drag to move diagram</div>
					<DxDiagram :simple-view="true" ref="diagram" height="100%" width="100%"
						:toolbox="{ visibility: 'disabled' }" :propertiesPanel="{ visibility: 'disabled' }"
						:historyToolbar="{ visible: false }" :viewToolbar="{ visible: false }"
						:editing="{ allowAddShape: false }"
						@selection-changed="diagramSelectionChanged" @request-edit-operation="diagramRequestEditOperation"
					>
						<DxNodes :data-source="diagramNodes" key-expr="id" text-expr="text" />
						<DxEdges :data-source="diagramEdges" key-expr="id" from-expr="from" to-expr="to" to-line-end-expr="toTip" text-expr="text" />
					</DxDiagram>
				</div>
			</DxItem>
		</DxSplitter>
	</div>
</template>

<style>
.dx-splitter .dx-splitter-item-content.dx-diagram {
	overflow-x: hidden;
}
.blueprint-tree-container {
	display: flex;
	flex-direction: row;
	height: 100%;
	column-gap: 10px;
}
.blueprint-tree {
	flex-shrink: 0;
}
.blueprint-tree .blueprint-keyspec {
	font-style: italic;
	color: #555;
	padding-left: 5px;
}
.blueprint-tree .dx-item-content i {
	margin-right: 5px;
	font-size: 18px;
	vertical-align: middle;
}
.blueprint-tree .newFieldLink {
	color: #555;
	font-style: italic;
}
.blueprint-tree .dx-treeview-item-without-checkbox.dx-state-selected > .dx-treeview-item {
	border-left: 10px solid var(--dx-color-primary);
}
.blueprint-editor-container {
	flex-grow: 1;
	flex-shrink: 1;
	border: 1px solid #ccc;
	padding: 10px;
}
</style>

<script setup>
/* eslint-disable */
import 'devexpress-diagram/dist/dx-diagram.min.css';
import { ref, computed, inject, watch, nextTick } from 'vue';
import { DxSortable } from 'devextreme-vue/sortable';
import { DxSplitter, DxItem } from 'devextreme-vue/splitter';
import { DxTreeView } from 'devextreme-vue/tree-view';
import { DxDiagram, DxNodes, DxEdges } from 'devextreme-vue/diagram';
import { DxDropDownButton } from 'devextreme-vue';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxButton } from 'devextreme-vue/button';
import CustomStore from 'devextreme/data/custom_store';
import ArrayStore from 'devextreme/data/array_store';
import notify from 'devextreme/ui/notify';
import DxTabs from 'devextreme-vue/tabs';
import { confirm as dxConfirm } from 'devextreme/ui/dialog';
import { onBeforeRouteLeave } from 'vue-router';
import FieldCreator from '../components/field-creator.vue';
import FieldEditor from '../components/field-editor.vue';
import SchemaEditor from '../components/schema-editor.vue';
import { fieldTypeMeta } from '../fields.js';
import { schemaListToTree } from '@/schemas-management';
import appInfo from '@/app-info';
import { SchemaCache } from '@/schemas';

const $http = inject('$http');
const isBusy = ref(false);					// If we are busy with a schema/field request, don't allow other actions.
const selectedItem = ref(null);				// Selected item in our blueprint tree, or null if none. May be a schema or field - will be cloned so can be mutated.
const selectedItemIsSchema = computed(() => selectedItem.value && !selectedItem.value.keyspec.includes('.'));
const selectedItemIsNewField = computed(() => selectedItem.value && selectedItem.value.internal && selectedItem.value.keyspec.endsWith('._new'));
const blueprintTreeComponent = ref(null);
const diagramRenderData = ref(null);		// Schema values to render our diagram from, as set from schemaTree.

// References to our editor components for checking for changes before navigating/switching selections.
const editorField = ref(null);
const editorSchema = ref(null);
const hasDirtyEditors = computed(() => {
	return Boolean(editorField.value?.isDirty) || Boolean(editorSchema.value?.isDirty);
});

// Add navigation guard before leaving the page to prompt if changes are outstanding.
onBeforeRouteLeave((to, from, next) => {
	if (!hasDirtyEditors.value) {
		next();
		return;
	}
	dxConfirm('You have unsaved changes. Do you really want to leave?', 'Discard changes?').then(result => {
		next(!!result);
	});
});

// Keyspec visiblity: show the keyspec (customer.name) in the blueprint tree, partially or not at all. Remembers setting from local storage.
const keyspecVisibility = ref(localStorage.getItem(`${appInfo.localPrefix}KeyspecViz`) || 'partial');
const keyspecVisibilityOptions = [
	{ id: 'show', icon: 'contains', text: 'All keys shown' },
	{ id: 'partial', icon: 'endswith', text: 'Short keys shown' },
	{ id: 'hide', icon: 'doesnotcontain', text: 'No keys shown' },
];
const keyspecVisibilityDisplay = computed(() => {
	const option = keyspecVisibilityOptions.find(opt => opt.id === keyspecVisibility.value);
	return !option ? { text: 'Show Keys', icon: 'contains' } : option;
});
watch(keyspecVisibility, (newValue) => {
	localStorage.setItem(`${appInfo.localPrefix}KeyspecViz`, newValue);
});

// Schema tree data source used in the blueprint tree.
const schemaTree = new CustomStore({
	key: 'keyspec', loadMode: 'raw', remoteOperations: false,
	load: () => {
		console.log("Loading schema tree");
		return $http.get('/schema?fields=1').then(response => {
			return schemaListToTree(response.data);
		});
	},
	onLoaded: (result) => {
		console.log("Schema tree loaded", result);
		diagramRenderData.value = result;
		diagram.value.instance.getNodeDataSource().reload();
		diagram.value.instance.getEdgeDataSource().reload();
	}
});

// Event handler for when blueprint tree is clicked: set selectedItem so we can edit the field / add new field, hint on diagram for selected blueprint.
const changingSelectedItem = ref(false);					// (we need to keep track of this so we don't immediately set a dirty flag when loading values)
const blueprintTreeSelected = async (event) => {
	if (!(event.event && event.event.type == 'dxclick'))	// Only trigger on an actual click, not automated selection changes.
		return;
	if (hasDirtyEditors.value) {
		const result = await dxConfirm('You have unsaved changes. Do you really want to leave?', 'Discard changes?');
		if (!result) {
			blueprintTreeComponent.value.instance.selectItem(selectedItem.value.keyspec);		// Reset selection in treeview to previously selected item.
			return;
		}
	}
	
	changingSelectedItem.value = true;
	selectedItem.value = null;
	
	// For some reason, directly setting the value here doesn't function when we have MULTIPLE dxForms - switching from editing to creating forms causes errors.
	// Other weird bugs as well that might have to do with some repainting issues. So we use nextTick to force the value to null for one tick before setting the new value.
	nextTick(() => {
		selectedItem.value = JSON.parse(JSON.stringify(event.itemData));		// Clone the item so we don't mutate the original.
		// Move items from the 'spec' field into the main object for access.
		if (selectedItem.value.spec) {
			for (var key in selectedItem.value.spec) {
				if (selectedItem.value.spec.hasOwnProperty(key) && !selectedItem.value.hasOwnProperty(key)) {
					selectedItem.value[key] = selectedItem.value.spec[key];
				}
			}
			delete selectedItem.value.spec;
		}

		nextTick(() => {
			changingSelectedItem.value = false;
		});
	});

	/*
	let diagramItem = diagram.value.instance.getItemByKey(schema);
	if (diagramItem) {
		// If we have clicked on an internal (Add field...) item, trigger this instead.
		if (event.itemData && event.itemData.internal) {
			console.log(event.itemData);
		}
		diagram.value.instance.setSelectedItems([ diagramItem ]);
		diagram.value.instance.scrollToItem(diagramItem);
		let guessComponent = diagram.value.instance['_$element'][0];
		if (guessComponent) {
			guessComponent = guessComponent.querySelectorAll('g.shape[data-key="' + diagramItem.id + '"] > rect');
			guessComponent = Array.from(guessComponent).filter(c => !c.classList.contains('selector'));
			guessComponent.forEach(o => o.style.fill = 'var(--dx-color-primary)');
			setTimeout(() => {
				guessComponent.forEach(o => o.style.fill = '');
			}, 500);
		}
	}
	*/
}

// When a new field is created or edited, we can refresh our existing blueprints list entirely to show it, and show a toast.
// TODO: Unify and deal with keeping the same selected item or collapse settings.
async function refreshAndResetTree(msg) {
	SchemaCache.fetchSchemaList();
	await SchemaCache.waitForData();
	blueprintTreeComponent.value.instance.getDataSource().reload();
	notify({ message: msg, type: "success", displayTime: 2000, position: "top right" });
	nextTick(() => {
		selectedItem.value = null;
	});
}
const createdNewField = () => refreshAndResetTree("New field created");			// TODO: This also happens with relationship field that creates a new schema.
const editedField = () => refreshAndResetTree("Field settings updated");
const changedSchema = () => refreshAndResetTree("Blueprint saved");
const deletedField = () => refreshAndResetTree("Field deleted");
const deletedSchema = () => refreshAndResetTree("Blueprint deleted");

// Drag and drop handlers for blueprint tree reordering via DxSortable.
const treeDragChange = function(e) {
	// console.log("Tree drag change", e);
};
const treeDragEnd = function(e) {
	// console.log("Tree drag end", e);
};









const diagramSelectionChanged = (event) => {
	console.log("Diagram selection changed", event);
}

const diagramRequestEditOperation = (event) => {
	if (event.operation.startsWith('delete') || event.operation.startsWith('add')) {
		event.allowed = false;
	}
	if (event.operation == 'changeConnection' && event.reason == 'modelModification') {
		// console.log("Diagram change connection", event);
		// event.allowed = false;
	}
}


const diagram = ref();
const diagramNodes = new CustomStore({
	key: 'id', loadMode: 'raw', remoteOperations: false,
	load: () => {
		if (!diagramRenderData.value)
			return [];
		let result = diagramRenderData.value.filter(s => !s.internal).map(s => ({
			id: s.keyspec,
			text: s.singular
		}));
		return result;
	},
	insert: () => {},
	update: () => {},
	remove: () => {},
});
const diagramEdges = new CustomStore({
	key: 'id', loadMode: 'raw', remoteOperations: false,
	load: () => {
		if (!diagramRenderData.value)
			return [];
		let relationIds = [];
		diagramRenderData.value.filter(s => !s.internal && s.fields).forEach(s => {
			let relations = s.fields.filter(f => f.fieldtype == 'relationship');
			relationIds.push(...relations.map(r => r.spec.relation));
		});
		relationIds = relationIds.filter(Boolean);
		relationIds = [...new Set(relationIds)];			// RelationIds is an array of integer IDs, but we will have two of each so we need to make them unique.
		// TODO: Don't use SchemaCache here, we need to get fresh details from API
		let relations = relationIds.map(id => SchemaCache.getSchemaById(id)).filter(Boolean);
		relations = relations.map(r => ({
			id: r.id,
			from: SchemaCache.getSchemaByType(r.meta.aType[0]).keyspec,
			to: SchemaCache.getSchemaByType(r.meta.bType[0]).keyspec,
			text: r.singular,
			toTip: 'none',
		}));

		console.log(relations);
		return relations;
	},
	insert: () => {},
	update: () => {},
	remove: () => {},
});

const resizedSplitter = () => {
	diagram.value.instance.fitToWidth();		// Or we could try repaint()? but this might nuke the history, etc.
}

const dragHandler = (ev) => {
	// console.log('draghandler', ev);
	if (ev.type == 'dragstart') {
		// Drag started, assign data of schema to our event
		ev.dataTransfer.setData('text/plain', ev.target.dataset['id']);
	} else if (ev.type == 'dragover') {
		ev.dataTransfer.dropEffect = "move";
		ev.preventDefault();
	}
}
const dropHandler = (ev) => {
	// console.log('drop', ev);
	ev.preventDefault();
	const data = ev.dataTransfer.getData("text/plain");
	diagramNodes.insert({ id: data + '_', text: data }).then(() => {
		diagram.value.instance.getNodeDataSource().reload();
	})
}

</script>
